import { environment } from '$env/environment';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { LocalizeRouterService } from 'localize-router';
import { BehaviorSubject, merge } from 'rxjs';
import { first, map, filter, distinctUntilChanged } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly localStorageKey =
    environment.localStorageKeys.lang || 'lang';
  private langs = environment.langs;

  /**
   * Subject for current language.
   *
   * @internal
   */
  private readonly currentLangSubject = new BehaviorSubject<string>(
    this.getSeedLang()
  );

  /**
   * Observable for current language.
   *
   * @public
   */
  public readonly currentLang$ = this.currentLangSubject.asObservable();

  constructor(
    private translateService: TranslateService,
    private localizeRouterService: LocalizeRouterService,
    private router: Router
  ) {
    this.translateService.addLangs(this.langs);

    merge(
      this.localizeRouterService.routerEvents,
      this.translateService.onLangChange.pipe(map((e) => e.lang))
    )
      .pipe(
        filter((lang): lang is string => Boolean(lang)),
        distinctUntilChanged()
      )
      .subscribe((lang) => this.setCurrentLang(lang));

    if (!environment.production) {
      this.router.events
        .pipe(first((event) => event instanceof NavigationEnd))
        .subscribe(() => {
          try {
            this.debugOutput();
          } catch (error) {
            console.error('Debug output error', error);
          }
        });
    }
  }

  public get currentLang() {
    return this.translateService.currentLang;
  }

  public get browserLang() {
    return this.translateService.getBrowserLang();
  }

  public get availableLangs() {
    return this.translateService.getLangs();
  }

  public get initialLang() {
    return (
      localStorage.getItem(this.localStorageKey) ||
      this.browserLang ||
      this.availableLangs[0]
    );
  }

  private getSeedLang(): string {
    return (
      this.localizeRouterService?.parser?.currentLang ||
      localStorage.getItem(this.localStorageKey) ||
      this.currentLang ||
      this.translateService.defaultLang
    );
  }

  private setCurrentLang(lang: string) {
    this.currentLangSubject.next(lang);
  }

  public use(lang: string) {
    this.localizeRouterService.changeLanguage(lang);
  }

  public change(lang: string) {
    if (!lang || lang === this.currentLang) {
      return;
    }

    localStorage.setItem(this.localStorageKey, lang);
    this.setCurrentLang(lang);
    this.localizeRouterService.changeLanguage(lang);
  }

  public traslateCurrentURL(lang: string) {
    let url: string;

    const href =
      window.location.href + (window.location.href.endsWith('/') ? '' : '/');

    try {
      if (this.currentLang === this.translateService.getDefaultLang()) {
        url = `${window.location.origin}/${lang}${this.router.url}`;
      } else if (lang === this.translateService.defaultLang) {
        url = `${href.replace(
          `/${this.localizeRouterService.parser.currentLang}/`,
          '/'
        )}`;
      } else {
        url = `${href.replace(
          `/${this.localizeRouterService.parser.currentLang}/`,
          `/${lang}/`
        )}`;
      }
    } catch (error) {
      console.error(error);
    }

    url = url.replace(/\/$/, ''); // trim ending slash

    return url;
  }

  public translateRoute(route: string): string {
    if (!route.startsWith('/')) {
      route = '/' + route;
    }

    return (<string>this.localizeRouterService.translateRoute(route)).replace(
      /^\/+/g,
      '/'
    ) as string;
  }

  private debugOutput(): void {
    try {
      console.log(
        'Available languages ' +
          this.translateService
            .getLangs()
            .map((e) => e.toUpperCase())
            .join(', ')
      );

      console.log(
        'Default language ' + this.translateService.defaultLang.toUpperCase()
      );
      console.log(
        'Router default language ' +
          this.localizeRouterService.parser.defaultLang.toUpperCase()
      );

      console.log(
        'Language ' + this.translateService.currentLang.toUpperCase()
      );
      console.log(
        'Router language ' +
          this.localizeRouterService.parser.currentLang.toUpperCase()
      );

      this.translateService.onLangChange.subscribe((e: LangChangeEvent) => {
        console.log('Language changed to ' + e.lang.toUpperCase());
      });
      this.localizeRouterService.routerEvents.subscribe((language: string) => {
        console.log('Router language changed to', language.toUpperCase());
      });
    } catch (error) {
      console.log(error);
    }
  }
}
